import { inject, Injectable } from "@angular/core";
import { Database } from "../../database/database";
import { Config } from "../../config/config";
import { DatabaseResolverService } from "../../database/database-resolver.service";
import { ConfirmationDialogService } from "../../common-components/confirmation-dialog/confirmation-dialog.service";
import { LOCATION_TOKEN, LOCAL_STORAGE_TOKEN } from "../../../utils/di-tokens";
import { Logging } from "../../logging/logging.service";

/**
 * Create and load backups of the database.
 */
@Injectable({
  providedIn: "root",
})
export class BackupService {
  private readonly localStorage = inject(LOCAL_STORAGE_TOKEN);
  private dbResolver = inject(DatabaseResolverService);

  private db: Database;

  private readonly confirmationDialog = inject(ConfirmationDialogService);
  private readonly location = inject(LOCATION_TOKEN);

  constructor() {
    this.db = this.dbResolver.getDatabase();
    // WARNING: currently only the default "app" database is backed up
  }

  /**
   * Creates an array holding all elements of the database.
   * This method can be used to create a backup of the data.
   */
  async getDatabaseExport(): Promise<any[]> {
    return await this.db.getAll();
  }

  /**
   * Removes all but the config of the database
   *
   * @returns Promise<any> a promise that resolves after all remove operations are done
   */
  async clearDatabase(): Promise<void> {
    const allDocs = await this.db.getAll();
    for (const row of allDocs) {
      if (row._id.startsWith(Config.ENTITY_TYPE + ":")) {
        // skip config in order to not break login!
        continue;
      }
      await this.db.remove(row);
    }
  }

  /**
   * Fills the database with the provided JSON data.
   * Data should be generated by the `getJsonExport` function
   *
   * @param documents An array of documents/raw entities to be directly written to the database
   * @param forceUpdate should existing objects be overridden? Default false
   */
  async restoreData(documents: any[], forceUpdate = false): Promise<void> {
    for (const record of documents) {
      // Remove _rev so CouchDB treats it as a new rather than a updated document
      delete record._rev;
      await this.db.put(record, forceUpdate);
    }
  }

  /**
   * sessionStorage key used to signal that a reset is pending.
   * Set before page reload; checked on next bootstrap in {@link runPendingReset}.
   */
  static readonly RESET_PENDING_KEY = "__RESET_PENDING";

  async resetApplication() {
    const choice = await this.confirmationDialog.getConfirmation(
      $localize`:Reset Application Confirmation:Reset Application`,
      $localize`:Reset Application Confirmation:Are you sure you want to reset the application? This will delete all application data from your device and you will have to synchronize again.`,
    );
    if (!choice) {
      return;
    }

    // deleting ALL local data (incl. possibly unsynced docs) - log for traceability of possible data loss
    Logging.warn(
      "Resetting application: user confirmed deletion of all local data",
    );

    // Reload the page first to kill all PouchDB connections, in-flight sync,
    // view indexing, and other async operations. IDB databases are deleted on
    // the fresh page (before Angular bootstraps) where no connections exist,
    // avoiding race conditions with PouchDB's internal IDB transactions.
    this.localStorage.clear();
    sessionStorage.setItem(BackupService.RESET_PENDING_KEY, "1");
    this.location.pathname = "";
  }

  /**
   * Run pending reset cleanup before Angular bootstraps.
   * Called from main.ts so that IndexedDB databases are deleted while
   * no PouchDB connections are open (eliminating race conditions).
   */
  static async runPendingReset(): Promise<void> {
    if (!sessionStorage.getItem(BackupService.RESET_PENDING_KEY)) {
      return;
    }
    sessionStorage.removeItem(BackupService.RESET_PENDING_KEY);
    DatabaseResolverService.clearLastSyncMarkers();

    // Delete all IndexedDB databases
    // (keep Sentry's offline queue so pending diagnostic logs about the reset
    // itself still reach remote logging after the reload)
    const dbs = await indexedDB.databases();
    await Promise.all(
      dbs
        .filter(({ name }) => name !== "sentry-offline")
        .map(
          ({ name }) =>
            new Promise<void>((resolve, reject) => {
              const del = indexedDB.deleteDatabase(name);
              del.onsuccess = () => resolve();
              del.onerror = () => reject(del.error);
            }),
        ),
    );

    // Unregister all service workers
    if (navigator.serviceWorker) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((reg) => reg.unregister()));
    }
  }
}
